import type { ButtonProps } from './types';

import React, { forwardRef } from 'react';

import clsx from 'clsx';
import Link from 'next/link';

import { button } from './styles';
import { Spinner } from '../../../atoms/Spinner';
import { Typography } from '../../../atoms/Typography';
import { Icon } from '../../../atoms/icons';

/**
 * Button component
 *
 * @param props - Component properties
 * @param props.children - Content to be displayed inside the button
 * @param props.buttonType='brand' - Type of the button
 * @param props.disabled - Whether the button is disabled
 * @param props.onClick - Click event handler
 * @param props.className - Additional class names for the button
 * @param props.href - URL to navigate to when the button is clicked
 * @param props.type - Button type attribute
 * @param props.loading=false - Whether the button shows a loading spinner
 * @param props.leftIconProps - Properties for the left icon
 * @param props.rightIconProps - Properties for the right icon
 * @param props.fontFace='LabelSemiboldLg' - Font face for the button text
 * @param props.radius - Border radius of the button
 * @param props.spaceX - Horizontal spacing around the button
 * @param props.spaceY - Vertical spacing around the button
 * @param props.gap - Gap between elements inside the button
 * @param props.isFullWidth - Whether the button is full width
 *
 * @returns The Button component
 **/
const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(
  (props, ref) => {
    const {
      children,
      onClick,
      className,
      href,
      type,
      loading = false,
      disabled,
      buttonType,
      radius,
      spaceX,
      spaceY,
      gap,
      leftIconProps,
      rightIconProps,
      isFullWidth,
      size,
      ...rest
    } = props;

    const leftIconColorAttr = leftIconProps?.iconColorAttr || 'fill';

    const rightIconColorAttr = rightIconProps?.iconColorAttr || 'fill';

    const hasIcon = !!leftIconProps || !!rightIconProps;

    const {
      base,
      leftIconStroke,
      leftIconFill,
      rightIconStroke,
      rightIconFill,
      text,
      spinner
    } = button({
      buttonType,
      radius,
      spaceX,
      spaceY,
      disabled,
      gap,
      isFullWidth,
      loading,
      size,
      hasIcon
    });

    const Content = () => (
      <>
        {loading ? (
          <span>
            <Spinner className={spinner()} size={size} />
          </span>
        ) : (
          <></>
        )}

        {leftIconProps ? (
          <Icon
            width={16}
            height={16}
            {...leftIconProps}
            className={clsx(
              leftIconColorAttr === 'fill' ? leftIconFill() : leftIconStroke(),
              leftIconProps.className,
              loading ? 'hidden' : ''
            )}
          />
        ) : (
          <></>
        )}
        <Typography className={clsx(text(), loading ? 'hidden' : '')}>
          {children}
        </Typography>
        {rightIconProps ? (
          <Icon
            {...rightIconProps}
            className={clsx(
              rightIconColorAttr === 'fill'
                ? rightIconFill()
                : rightIconStroke(),
              rightIconProps.className,
              loading ? 'hidden' : ''
            )}
          />
        ) : (
          <></>
        )}
      </>
    );

    if (href) {
      return (
        <Link
          ref={ref as React.Ref<HTMLAnchorElement>}
          href={href}
          className={clsx(base(), className)}
          onClick={(e) => {
            if (onClick) {
              onClick(e);
            }
          }}
          {...rest}
        >
          <Content />
        </Link>
      );
    }

    return (
      <button
        ref={ref as React.Ref<HTMLButtonElement>}
        className={clsx(base(), className)}
        onClick={onClick}
        disabled={disabled}
        type={type}
        {...rest}
      >
        <Content />
      </button>
    );
  }
);

Button.displayName = 'Button';

export { Button, button as buttonVariants };
export type { ButtonProps };
